import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { readPref, writePref } from './storage';

export const THEME_CHOICES = ['auto', 'light', 'dark'] as const;
export type ThemeChoice = (typeof THEME_CHOICES)[number];

export const THEME_LABELS: Record<ThemeChoice, string> = {
  auto: 'Automático',
  light: 'Claro',
  dark: 'Escuro',
};

interface ThemeValue {
  choice: ThemeChoice;
  setChoice: (c: ThemeChoice) => void;
  dark: boolean;
  /** Class for every `.dz` root, e.g. "dz dark". */
  dz: string;
}

const ThemeContext = createContext<ThemeValue | null>(null);

function systemDark(): boolean {
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function initialChoice(): ThemeChoice {
  const saved = readPref('theme');
  return saved === 'light' || saved === 'dark' || saved === 'auto' ? saved : 'auto';
}

/** Light/dark theme per device. "Automático" follows the device setting. */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [choice, setChoiceState] = useState<ThemeChoice>(initialChoice);
  const [sysDark, setSysDark] = useState(systemDark);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (e: MediaQueryListEvent) => setSysDark(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const dark = choice === 'dark' || (choice === 'auto' && sysDark);

  useEffect(() => {
    document.body.classList.toggle('theme-dark', dark);
  }, [dark]);

  const value = useMemo<ThemeValue>(
    () => ({
      choice,
      setChoice: (c) => {
        writePref('theme', c);
        setChoiceState(c);
      },
      dark,
      dz: dark ? 'dz dark' : 'dz',
    }),
    [choice, dark],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme outside ThemeProvider');
  return ctx;
}
